import { tr } from "./language";
/**
 * Shared image-picker helper with validation.
 *
 * Usage:
 *   const res = await pickAndValidateImage({ minWidth: 400, minHeight: 400 });
 *   if (res) { use res.file, res.dataUrl, res.width, res.height }
 *
 * On any validation failure, shows a sonner toast and resolves to null.
 */
import { toast } from "sonner";

export const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
export const IMAGE_ACCEPT_LIST = ["image/jpeg", "image/png", "image/webp"];

export const DEFAULT_MAX_BYTES = 20 * 1024 * 1024; // High-resolution master limit

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadDimensions = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });

/**
 * @param {object} opts
 * @param {number} [opts.minWidth]
 * @param {number} [opts.minHeight]
 * @param {number} [opts.maxBytes=20 MB]
 * @param {string} [opts.labelLogo]  human-readable label used in error toast
 */
export async function pickAndValidateImage(opts = {}) {
  const {
    minWidth = 0,
    minHeight = 0,
    maxBytes = DEFAULT_MAX_BYTES,
    label = "image",
  } = opts;

  const file = await new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = IMAGE_ACCEPT;
    input.onchange = (e) => resolve(e.target.files?.[0] || null);
    input.click();
  });
  if (!file) return null;

  if (!IMAGE_ACCEPT_LIST.includes(file.type)) {
    toast.error(tr("Unsupported format"), {
      description: tr("Please pick a JPEG, PNG, or WebP image."),
    });
    return null;
  }
  if (file.size > maxBytes) {
    const mb = (maxBytes / (1024 * 1024)).toFixed(1);
    toast.error(tr("{{value0}} is too large", { value0: label }), {
      description: tr("Please pick a file under {{value0}} MB.", { value0: mb }),
    });
    return null;
  }

  const dataUrl = await readFileAsDataUrl(file);
  let width = 0;
  let height = 0;
  try {
    ({ width, height } = await loadDimensions(dataUrl));
  } catch {
    toast.error(tr("Couldn't read image"), {
      description: tr("The file looks corrupted. Try a different image."),
    });
    return null;
  }

  if ((minWidth && width < minWidth) || (minHeight && height < minHeight)) {
    toast.error(tr("{{value0}} is too small", { value0: label }), {
      description: tr("Please use at least {{value0}}×{{value1}} px (you picked {{value2}}×{{value3}}).", { value0: minWidth, value1: minHeight, value2: width, value3: height }),
    });
    return null;
  }

  return { file, dataUrl, width, height };
}
